import { Box, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ReactNode } from 'react'

import planElementHorizontal from '@/assets/images/plan-element-horizontal.png'
import planElementVertical from '@/assets/images/plan-element-vertical.png'
import planStartElementHorizontal from '@/assets/images/plan-start-element-horizontal.png'
import planStartElementVertical from '@/assets/images/plan-start-element-vertical.png'
import type { Plan } from '@/services/api/api.types'
import { calculateUserRanges, formatMaxUsers, formatPlanPrice, formatUserRange } from '@/utils/planUtils'

const SEGMENT_HEIGHT_PX = 200
const SEGMENT_HEIGHT_PX_MEDIUM = 140
const PLAN_START_ASPECT = 1080 / 990
const PLAN_ELEMENT_ASPECT = 1080 / 952
const SEGMENT_OVERLAP_PX = 16
const SEGMENT_OVERLAP_PX_MEDIUM = 12

// Vertical assets: 1080×1179 (start), 1080×1226 (element) → height/width
const PLAN_START_ASPECT_VERTICAL = 1179 / 1080
const PLAN_ELEMENT_ASPECT_VERTICAL = 1226 / 1080
const MOBILE_CARD_WIDTH_PX = 280
const VERTICAL_OVERLAP_PX = 24

interface PricingTiersDisplayProps {
  plans: Plan[]
  actions?: ReactNode
  currentUserCount?: number
}

export function PricingTiersDisplay({ plans, actions, currentUserCount }: PricingTiersDisplayProps) {
  const theme = useMantineTheme()
  const isMobile = !useMediaQuery(`(min-width: ${theme.breakpoints.sm})`)
  const isMediumOrNarrower = !useMediaQuery(`(min-width: ${theme.breakpoints.md})`)

  const activePlans =
    plans
      ?.filter(plan => plan.active)
      .sort((a, b) => {
        const aMax = a.maxUsers === 0 ? Infinity : a.maxUsers
        const bMax = b.maxUsers === 0 ? Infinity : b.maxUsers
        return aMax - bMax
      }) || []

  const userRanges = calculateUserRanges(activePlans)

  const segmentHeight = isMediumOrNarrower ? SEGMENT_HEIGHT_PX_MEDIUM : SEGMENT_HEIGHT_PX
  const overlapPx = isMediumOrNarrower ? SEGMENT_OVERLAP_PX_MEDIUM : SEGMENT_OVERLAP_PX

  return (
    <Stack mt="xl">
      <Stack gap="xl">
        <Stack gap="xs" align="center">
          <Title order={2} ta="center">
            Our Pricing Tiers
          </Title>
          <Text size="md" c="dimmed" ta="center">
            Pricing is based on the number of users. The more users you have, the more you save per user.
          </Text>
          {currentUserCount != null && (
            <Text size="sm" c="dimmed" ta="center">
              You currently have{' '}
              <Text span fw={700}>
                {currentUserCount} users
              </Text>
            </Text>
          )}
        </Stack>

        <Box
          style={{
            overflowX: isMobile ? 'visible' : 'auto',
            overflowY: 'visible',
            paddingBottom: 8,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          {isMobile ? (
            <Stack gap={0} align="center" style={{ width: '100%', maxWidth: MOBILE_CARD_WIDTH_PX }}>
              {activePlans.map((plan, index) => {
                const range = userRanges[index]
                const isFirst = index === 0
                const userRangeLabel = isFirst ? formatMaxUsers(plan.maxUsers) : formatUserRange(range.min, range.max)
                const isFree = plan.priceDollars === 0
                const heightPx =
                  MOBILE_CARD_WIDTH_PX * (isFirst ? PLAN_START_ASPECT_VERTICAL : PLAN_ELEMENT_ASPECT_VERTICAL)

                return (
                  <Box
                    key={plan.bundleId}
                    style={{
                      position: 'relative',
                      width: MOBILE_CARD_WIDTH_PX,
                      height: heightPx,
                      marginTop: index === 0 ? 0 : -VERTICAL_OVERLAP_PX,
                      backgroundImage: `url(${isFirst ? planStartElementVertical : planElementVertical})`,
                      backgroundSize: '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      zIndex: index,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2,
                    }}>
                    <Stack py="5rem" align="center" justify="space-between" style={{ flex: 1 }}>
                      <Text size="1.5rem" c="dark.6" fw={400}>
                        {userRangeLabel}
                      </Text>
                      <Stack align="center" justify="center" gap={0}>
                        <Text size="3.0rem" fw={700} c="dark.8">
                          {formatPlanPrice(plan.priceDollars)}
                        </Text>
                        <Text size="lg" c="dark.6" fw={700} opacity={isFree ? 0 : 1}>
                          per user / month
                        </Text>
                      </Stack>
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          ) : (
            <Group justify="center" align="stretch" gap={0} style={{ flexShrink: 0 }} wrap="nowrap">
              {activePlans.map((plan, index) => {
                const range = userRanges[index]
                const isFirst = index === 0
                const userRangeLabel = isFirst ? formatMaxUsers(plan.maxUsers) : formatUserRange(range.min, range.max)
                const isFree = plan.priceDollars === 0

                const widthPx = isFirst ? segmentHeight * PLAN_START_ASPECT : segmentHeight * PLAN_ELEMENT_ASPECT

                return (
                  <Box
                    key={plan.bundleId}
                    style={{
                      position: 'relative',
                      height: segmentHeight,
                      width: widthPx,
                      minWidth: widthPx,
                      marginLeft: index === 0 ? 0 : -overlapPx,
                      backgroundImage: `url(${isFirst ? planStartElementHorizontal : planElementHorizontal})`,
                      backgroundSize: '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      zIndex: index,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2,
                    }}>
                    <Stack py="xl" align="center" justify="space-between" style={{ flex: 1 }}>
                      <Text size={isMediumOrNarrower ? 'md' : 'xl'} c="dark.6" fw={400}>
                        {userRangeLabel}
                      </Text>
                      <Stack align="center" justify="center" gap={0}>
                        <Text size={isMediumOrNarrower ? '1.75rem' : '2.5rem'} fw={700} c="dark.8">
                          {formatPlanPrice(plan.priceDollars)}
                        </Text>
                        <Text size={isMediumOrNarrower ? 'xs' : 'md'} c="dark.6" fw={700} opacity={isFree ? 0 : 1}>
                          per user / month
                        </Text>
                      </Stack>
                    </Stack>
                  </Box>
                )
              })}
            </Group>
          )}
        </Box>

        <Text size="sm" c="dimmed" ta="center">
          All paid plans include priority support and advanced features. Prices shown are per user per month.
        </Text>
      </Stack>

      {actions && (
        <Group justify="center" mt="xl">
          {actions}
        </Group>
      )}
    </Stack>
  )
}
